/**
 * StoreManagerRoute — 매장대표만 통과하는 라우트 가드.
 *
 * 한 매장 안에서 도는 매장 전용 화면(직원 관리·데이터 가져오기 등)을 보호한다.
 * 매장 소속 owner(또는 디버그용 매장 소속 superadmin)만 통과시키고, 그 외(직원·본사 admin)는
 * "/"로 리다이렉트한다. 본사 admin 은 이 매장 전용 화면이 아니라 전 매장 관리(/admin/*)를 쓴다.
 * 백엔드 requireStoreManager 와 같은 기준이며, 데이터 격리는 서버가 호출자 매장으로 강제한다.
 *
 * 로딩 처리는 GlobalAdminRoute 와 동일한 패턴 — profile fetch 가 일시 장애로
 * 너무 오래 멈춰 있으면 무한 스피너 대신 복구 안내를 띄운다.
 */

import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../context/AuthContext";

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

function StalledNotice() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        서버 응답이 지연되고 있어요. 잠시 후 다시 시도해주세요.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        새로고침
      </button>
    </div>
  );
}

export default function StoreManagerRoute() {
  const { user, profile, loading, profileLoading, isStoreManager } = useAuth();

  // user는 있는데 profile이 12초 넘게 null이면 일시 장애로 보고 복구 안내를 띄운다.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!loading && !profileLoading && user && profile === null) {
      const t = setTimeout(() => setStalled(true), 12000);
      return () => clearTimeout(t);
    }
    setStalled(false);
  }, [loading, profileLoading, user, profile]);

  if (loading || profileLoading) return <Spinner />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (profile === null) return stalled ? <StalledNotice /> : <Spinner />;

  if (!isStoreManager()) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
